import React from "react";
import heroImage from "../../images/engineering-capability-hero.png";
import integratedExpertiseVisual from "../../images/integrated-engineering-expertise-visual.png";
import "./EngineeringCapabilitiesOverview.css";

type LinkItem = {
  title: string;
  description: string;
  href: string;
};

const coreServices: LinkItem[] = [
  {
    title: "ASIC & SOC Design Services",
    description:
      "Engineering solutions that transform product concepts into robust, scalable, and implementation-ready digital designs.",
    href: "/services/asic-soc-design",
  },
  {
    title: "Design Verification Services",
    description:
      "Comprehensive verification methodologies that improve functional correctness, strengthen design confidence, and support reliable silicon realization.",
    href: "/services/verification-engineering",
  },
  {
    title: "Physical Design",
    description:
      "Advanced implementation capabilities focused on optimizing performance, power, area, and manufacturability for successful silicon implementation.",
    href: "/services/physical-design",
  },
  {
    title: "Design for Test (DFT)",
    description:
      "Structured test engineering solutions that enhance manufacturability, improve fault coverage, and support production-ready semiconductor products.",
    href: "/services/dft",
  },
  {
    title: "Silicon Realization",
    description:
      "Integrated engineering support that enables the successful transition from implementation through validation to reliable silicon realization.",
    href: "/services/silicon-realization",
  },
];

const advancedDesign: LinkItem[] = [
  {
    title: "Analog & Mixed Signal Design & Layout",
    description:
      "Specialized analog and mixed-signal circuit design supporting precision interfaces, power management, data conversion, and high-performance semiconductor applications.",
    href: "/services/analog-mixed-signal",
  },
  {
    title: "Static Timing Analysis (STA)",
    description:
      "Comprehensive timing analysis and optimization to achieve timing closure, design integrity, and predictable silicon performance.",
    href: "/services/static-timing-analysis",
  },
  {
    title: "FPGA Engineering",
    description:
      "FPGA architecture, implementation, prototyping, and validation supporting accelerated product development and hardware verification.",
    href: "/services/fpga-engineering",
  },
];

const platformCapabilities: LinkItem[] = [
  {
    title: "Embedded Software Systems",
    description:
      "Embedded firmware and software development enabling seamless hardware-software integration across semiconductor and embedded platforms.",
    href: "/services/embedded-software-systems",
  },
  {
    title: "Hardware Platform Engineering",
    description:
      "Development of hardware platforms supporting product prototyping, validation, testing, and engineering development activities.",
    href: "/services/hardware-platform-engineering",
  },
  {
    title: "Networking Solutions",
    description:
      "Engineering expertise supporting networking infrastructure, communication technologies, connectivity platforms, and high-speed data movement solutions.",
    href: "/services/networking-solutions",
  },
];

const validationCapabilities: LinkItem[] = [
  {
    title: "Silicon Validation",
    description:
      "Post-silicon validation, debug, characterization, and bring-up supporting reliable product qualification and production readiness.",
    href: "/services/silicon-validation",
  },
  {
    title: "EDA & Design Automation",
    description:
      "Engineering automation solutions that improve design productivity, streamline engineering workflows, and enhance development efficiency.",
    href: "/services/eda-design-automation",
  },
  {
    title: "Product Engineering",
    description:
      "Comprehensive engineering support across product enhancement, lifecycle management, feature development, and long-term product sustainment.",
    href: "/services/product-engineering",
  },
];

const CapabilityCards = ({ items }: { items: LinkItem[] }) => (
  <div className="ecov-cap-grid">
    {items.map((item) => (
      <article className="ecov-cap-card" key={item.title}>
        <h4>{item.title}</h4>
        <p>{item.description}</p>
        <a href={item.href}>Learn More →</a>
      </article>
    ))}
  </div>
);

const EngineeringCapabilitiesOverview: React.FC = () => {
  return (
    <div className="ecov-page">

      {/* SECTION 1 */}
      <section className="ecov-section ecov-overview">
        <div className="ecov-shell ecov-overview-grid">
          <div className="ecov-copy">
            <h2>Engineering Capability Overview</h2>
            <p>
              Digicomm Semiconductor delivers integrated semiconductor engineering
              capabilities across the complete semiconductor product development
              lifecycle. Our multidisciplinary expertise combines Core Semiconductor
              Engineering Services, Specialized Engineering Capabilities, and flexible
              Engineering Engagement Models, enabling semiconductor organizations to
              access the right expertise, at the right stage, through the right
              engagement approach.
            </p>
          </div>

          <div className="ecov-overview-image">
            <img src={heroImage} alt="" />
          </div>
        </div>
      </section>

      {/* SECTION 2 */}
      <section className="ecov-section ecov-integrated">
        <div className="ecov-shell">
          <div className="ecov-copy ecov-copy-center">
            <h2>Integrated Engineering Expertise</h2>
            <p>
              Semiconductor product development is a collaborative engineering journey
              where every engineering discipline contributes to reliable silicon
              realization. From digital design and functional verification to physical
              implementation, design for test, and silicon realization, every stage plays
              a critical role in achieving successful product outcomes.
            </p>
            <p>
              Digicomm brings these specialized engineering capabilities together through
              a structured, collaborative, and execution-driven approach, enabling
              customers to engage the right expertise at the right stage of the
              semiconductor development lifecycle.
            </p>
          </div>

          <div className="ecov-integrated-image">
            <img src={integratedExpertiseVisual} alt="" />
          </div>
        </div>
      </section>

      {/* SECTION 3 */}
      <section className="ecov-section ecov-core">
        <div className="ecov-shell">
          <h2 className="ecov-section-heading">Our Core Engineering Services</h2>

          <div className="ecov-core-grid">
            {coreServices.map((item, index) => (
              <article className="ecov-core-card" key={item.title}>
                <div className="ecov-core-number">
                  {String(index + 1).padStart(2, "0")}
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <a href={item.href}>Learn More →</a>
              </article>
            ))}
          </div>

          <p className="ecov-note">
            These Core Engineering Services are complemented by specialized engineering
            capabilities that address domain-specific technologies, implementation
            requirements, and evolving semiconductor product development needs.
          </p>
        </div>
      </section>

      {/* SECTION 4 */}
      <section className="ecov-section ecov-specialized">
        <div className="ecov-shell">
          <div className="ecov-copy">
            <h2>SPECIALIZED ENGINEERING CAPABILITIES</h2>
            <p>
              Semiconductor product development extends beyond the core engineering
              disciplines. As technologies become increasingly integrated and product
              requirements more specialized, engineering teams often require focused
              expertise across adjacent domains. Digicomm complements its Core
              Semiconductor Engineering Services with specialized engineering
              capabilities that strengthen product development, accelerate execution,
              and support evolving semiconductor technologies through a single
              engineering partner.
            </p>
          </div>

          <div className="ecov-group">
            <div className="ecov-group-title">
              <h3>Advanced Design & Implementation :</h3>
              <p>
                Focused engineering capabilities supporting advanced semiconductor
                design, implementation, and optimization.
              </p>
            </div>
            <CapabilityCards items={advancedDesign} />
          </div>

          <div className="ecov-group">
            <div className="ecov-group-title">
              <h3>Software, Platforms & Infrastructure :</h3>
              <p>
                Engineering capabilities enabling hardware-software integration,
                platform development, and connectivity solutions.
              </p>
            </div>
            <CapabilityCards items={platformCapabilities} />
          </div>

          <div className="ecov-group">
            <div className="ecov-group-title">
              <h3>Validation, Automation & Product Engineering:</h3>
              <p>
                Capabilities supporting silicon qualification, engineering productivity,
                and long-term product realization.
              </p>
            </div>
            <CapabilityCards items={validationCapabilities} />
          </div>
        </div>
      </section>

      {/* SECTION 5 */}
      <section className="ecov-section ecov-engagement">
        <div className="ecov-shell">
          <div className="ecov-copy ecov-copy-center">
            <h2>ENGINEERING ENGAGEMENT MODELS</h2>
            <p>
              Every semiconductor program is unique. Engineering priorities, resource
              strategies, project timelines, and execution requirements often differ
              from one organization to another. Digicomm complements its engineering
              expertise with flexible engagement models that enable customers to access
              specialized capabilities in the way that best aligns with their business
              objectives. Whether strengthening an existing engineering team or
              delivering focused technical leadership, our engagement models are
              designed to provide flexibility, collaboration, and dependable execution.
            </p>
          </div>

          <div className="ecov-engagement-grid">
            <article className="ecov-engagement-card">
              <span>01</span>
              <h3>Engineering Consulting</h3>
              <p>
                Strategic engineering consulting that supports architecture reviews,
                technology assessment, design optimization, engineering advisory,
                project planning, and specialized technical guidance across
                semiconductor product development.
              </p>
              <a href="/services/engineering-consulting">Learn More →</a>
            </article>

            <article className="ecov-engagement-card">
              <span>02</span>
              <h3>Dedicated Engineering Teams</h3>
              <p>
                Dedicated semiconductor engineering teams that seamlessly integrate
                with customer organizations, providing scalable technical expertise,
                collaborative execution, and long-term engineering support across the
                complete semiconductor development lifecycle.
              </p>
              <a href="/services/dedicated-engineering-teams">Learn More →</a>
            </article>
          </div>
        </div>
      </section>

      {/* SECTION 6 */}
      <section className="ecov-section ecov-delivery">
        <div className="ecov-shell">
          <div className="ecov-copy">
            <h2>The Digicomm Engineering Delivery Framework</h2>
            <p>
              Successful semiconductor programs require more than technical
              expertise—they demand structured execution, seamless collaboration,
              engineering discipline, and a consistent commitment to quality. At
              Digicomm, every engagement is guided by a proven engineering delivery
              approach that aligns with customer objectives, integrates with existing
              engineering ecosystems, and delivers dependable outcomes across every
              stage of semiconductor product development.
            </p>
          </div>

          <h3 className="ecov-framework-title">
            Our Engineering Delivery Framework:
          </h3>

          <div className="ecov-framework-grid">
            <article>
              <span>01</span>
              <h4>Understand</h4>
              <p>
                Begin by understanding the customer's product vision, engineering
                objectives, technical challenges, development processes, and project
                expectations to establish complete engineering alignment.
              </p>
            </article>

            <article>
              <span>02</span>
              <h4>Integrate</h4>
              <p>
                Seamlessly integrate with customer engineering teams, development
                methodologies, tools, and workflows to function as a natural extension
                of the organization.
              </p>
            </article>

            <article>
              <span>03</span>
              <h4>Execute</h4>
              <p>
                Deliver engineering excellence through structured processes,
                specialized expertise, collaborative execution, and disciplined project
                management across every engineering engagement.
              </p>
            </article>

            <article>
              <span>04</span>
              <h4>Deliver</h4>
              <p>
                Enable reliable silicon outcomes through consistent quality,
                predictable execution, transparent communication, and long-term
                engineering partnership.
              </p>
            </article>
          </div>
        </div>
      </section>
    </div>
  );
};



export default EngineeringCapabilitiesOverview;
